import React, { useMemo, useEffect, useState } from "react";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import CustomDropdown from "../../CustomDropdown/CustomDropdown";
import styles from "./CustomDonutChart.module.css";

ChartJS.register(ArcElement, Tooltip, Legend);

/* ============================================================== *
 * CustomDonutChart
 * --------------------------------------------------------------
 * Three modes auto-detected from `data`:
 *   SINGLE-ARC   (Attendance Rate fraction — presentDays/workingDays)
 *   BREAKDOWN    (Academic Health Score — data.breakdown object)
 *   SEGMENTS     (3-color donut with counts — data.segments array)
 *
 * Segment colors come from --gradient-* CSS vars in app.css.
 * ============================================================== */

/* ---------- Gradient palette: indices map to gradient CSS vars ---------- */
const GRADIENT_PALETTE = [
  {
    var: "--gradient-violet",
    fallback: "linear-gradient(135deg, #7c4dff 0%, #b197fc 100%)",
  }, // 0
  {
    var: "--gradient-green-pair",
    fallback: "linear-gradient(135deg, #10b981 0%, #6ee7b7 100%)",
  }, // 1
  {
    var: "--gradient-pink-pair",
    fallback: "linear-gradient(135deg, #ec4899 0%, #f9a8d4 100%)",
  }, // 2
  {
    var: "--gradient-orange-pair",
    fallback: "linear-gradient(135deg, #f97316 0%, #fdba74 100%)",
  }, // 3
  {
    var: "--gradient-lime-pair",
    fallback: "linear-gradient(135deg, #acc236 0%, #d9ed92 100%)",
  }, // 4
  {
    var: "--gradient-red-pair",
    fallback: "linear-gradient(135deg, #ef4444 0%, #fca5a5 100%)",
  }, // 5
  {
    var: "--gradient-indigo-pair",
    fallback: "linear-gradient(135deg, #4338ca 0%, #818cf8 100%)",
  }, // 6
  {
    var: "--gradient-blue-pair",
    fallback: "linear-gradient(135deg, #2563eb 0%, #38bdf8 100%)",
  }, // 7
  {
    var: "--gradient-teal-pair",
    fallback: "linear-gradient(135deg, #166a8f 0%, #4bc0c0 100%)",
  }, // 8
  {
    var: "--gradient-violet-indigo",
    fallback: "linear-gradient(135deg, #7c4dff 0%, #4338ca 100%)",
  }, // 9
  {
    var: "--gradient-pink-red",
    fallback: "linear-gradient(135deg, #ec4899 0%, #ef4444 100%)",
  }, // 10
];

/* paletteIndex map:
 *   0 = violet | 1 = green | 2 = pink | 3 = orange | 4 = lime | 5 = red
 *   6 = indigo | 7 = blue  | 8 = teal | 9 = violet→indigo | 10 = pink→red
 */
const DEFAULT_SEGMENT_CONFIG = {
  examMarks: { label: "Exam Marks", paletteIndex: 1 },
  attendance: { label: "Attendance", paletteIndex: 2 },
  assessmentRemedial: { label: "Assessment & Remedial Score", paletteIndex: 3 },
  applicationUsage: { label: "Application Usage", paletteIndex: 0 },
  engagement: { label: "Engagement", paletteIndex: 4 },
  teacherSentiment: { label: "Teacher Sentiment", paletteIndex: 0 },
};

/* Status → palette index for SingleArcDonut filled arc */
const STATUS_PALETTE_INDEX = { green: 1, orange: 3, red: 5 };

const FONT_FAMILY =
  '"Open Sans", -apple-system, BlinkMacSystemFont, sans-serif';

function readToken(varName, fallback) {
  if (typeof window === "undefined") return fallback;
  const v = getComputedStyle(document.documentElement)
    .getPropertyValue(varName)
    .trim();
  return v || fallback;
}

function parseGradientStops(gradientString) {
  if (!gradientString) return [];
  const re = /#[0-9a-fA-F]{3,8}\b|rgba?\([^)]+\)/g;
  return gradientString.match(re) || [];
}

function resolveGradient(paletteIndex) {
  const entry = GRADIENT_PALETTE[paletteIndex] || GRADIENT_PALETTE[0];
  const cssString = readToken(entry.var, entry.fallback);
  const stops = parseGradientStops(cssString);
  const safeStops =
    stops.length >= 2 ? stops : parseGradientStops(entry.fallback);
  return {
    cssString,
    dark: safeStops[0] || "#cccccc",
    light: safeStops[1] || "#cccccc",
  };
}

function makeArcGradient(ctx, chartArea, dark, light) {
  if (!chartArea) return null;
  const gradient = ctx.createLinearGradient(
    chartArea.left,
    chartArea.top,
    chartArea.right,
    chartArea.bottom,
  );
  gradient.addColorStop(0, dark);
  gradient.addColorStop(1, light);
  return gradient;
}

/* ============================================================== *
 * Plugin 1 — segment weight labels with `%` suffix (BreakdownDonut)
 * ============================================================== */
const segmentLabelsPlugin = {
  id: "segmentLabels",
  afterDatasetsDraw(chart, _args, opts) {
    if (!opts) return;
    const { ctx, data } = chart;
    const meta = chart.getDatasetMeta(0);
    if (!meta?.data?.length) return;

    const color = opts.color || "#142454";
    const font = opts.font || `700 16px ${FONT_FAMILY}`;

    meta.data.forEach((arc, index) => {
      const value = data.datasets[0].data[index];
      if (!value) return;

      const { x, y, startAngle, endAngle, innerRadius, outerRadius } =
        arc.getProps([
          "x",
          "y",
          "startAngle",
          "endAngle",
          "innerRadius",
          "outerRadius",
        ]);

      const midAngle = (startAngle + endAngle) / 2;
      const radius = (innerRadius + outerRadius) / 2;
      const labelX = x + Math.cos(midAngle) * radius;
      const labelY = y + Math.sin(midAngle) * radius;

      ctx.save();
      ctx.fillStyle = color;
      ctx.font = font;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(`${value}%`, labelX, labelY);
      ctx.restore();
    });
  },
};

/* ============================================================== *
 * Plugin 2 — segment raw counts (no `%`) (SegmentsDonut)
 * ============================================================== */
const segmentCountPlugin = {
  id: "segmentCount",
  afterDatasetsDraw(chart, _args, opts) {
    if (!opts) return;
    const { ctx, data } = chart;
    const meta = chart.getDatasetMeta(0);
    if (!meta?.data?.length) return;

    const color = opts.color || "#ffffff";
    const font = opts.font || `700 14px ${FONT_FAMILY}`;

    meta.data.forEach((arc, index) => {
      const value = data.datasets[0].data[index];
      if (!value) return;

      const { x, y, startAngle, endAngle, innerRadius, outerRadius } =
        arc.getProps([
          "x",
          "y",
          "startAngle",
          "endAngle",
          "innerRadius",
          "outerRadius",
        ]);

      const midAngle = (startAngle + endAngle) / 2;
      const radius = (innerRadius + outerRadius) / 2;
      const labelX = x + Math.cos(midAngle) * radius;
      const labelY = y + Math.sin(midAngle) * radius;

      ctx.save();
      ctx.fillStyle = color;
      ctx.font = font;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(String(value), labelX, labelY);
      ctx.restore();
    });
  },
};

/* ============================================================== *
 * Plugin 3 — donut center content (shared by all three modes)
 * ============================================================== */
const donutCenterPlugin = {
  id: "donutCenter",
  afterDatasetsDraw(chart, _args, opts) {
    if (!opts) return;

    const { ctx } = chart;
    const meta = chart.getDatasetMeta(0);
    if (!meta?.data?.length) return;

    const arc = meta.data[0];
    const {
      x: centerX,
      y: centerY,
      innerRadius,
      outerRadius,
    } = arc.getProps(["x", "y", "innerRadius", "outerRadius"]);

    /* White center disc */
    ctx.save();
    ctx.shadowColor = "rgba(0, 0, 0, 0.08)";
    ctx.shadowBlur = 12;
    ctx.shadowOffsetY = 2;
    ctx.beginPath();
    ctx.arc(centerX, centerY, innerRadius * 0.88, 0, 2 * Math.PI);
    ctx.fillStyle = "#ffffff";
    ctx.fill();
    ctx.restore();

    /* Fraction mode (SingleArcDonut) */
    if (opts.fraction) {
      const {
        numerator,
        denominator,
        numeratorColor = "#142454",
        denominatorColor = "#999999",
      } = opts.fraction;

      const numFont = `700 24px ${FONT_FAMILY}`;
      const denomFont = `500 14px ${FONT_FAMILY}`;
      const denomText = "/" + denominator;

      ctx.save();
      ctx.textBaseline = "middle";

      ctx.font = numFont;
      const numWidth = ctx.measureText(String(numerator)).width;
      ctx.font = denomFont;
      const denomWidth = ctx.measureText(denomText).width;

      const totalWidth = numWidth + denomWidth;
      const startX = centerX - totalWidth / 2;

      ctx.textAlign = "left";
      ctx.font = numFont;
      ctx.fillStyle = numeratorColor;
      ctx.fillText(String(numerator), startX, centerY);

      ctx.font = denomFont;
      ctx.fillStyle = denominatorColor;
      ctx.fillText(denomText, startX + numWidth, centerY + 2);
      ctx.restore();
    }

    /* Composite score mode (BreakdownDonut) — renders "{value}%" */
    if (opts.score) {
      const { value, color = "#142454" } = opts.score;
      ctx.save();
      ctx.font = `700 28px ${FONT_FAMILY}`;
      ctx.fillStyle = color;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(String(value), centerX, centerY);
      ctx.restore();
    }

    /* Plain value mode (SegmentsDonut) — renders raw number, no % */
    if (opts.plainValue) {
      const { value, color = "#142454" } = opts.plainValue;
      ctx.save();
      ctx.font = `700 28px ${FONT_FAMILY}`;
      ctx.fillStyle = color;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(String(value), centerX, centerY);
      ctx.restore();
    }

    /* Arc label — drawn at midpoint of first filled arc (SingleArcDonut) */
    if (opts.arcLabel) {
      const { value, color = "#ffffff" } = opts.arcLabel;
      const filledArc = meta.data[0];
      const { startAngle, endAngle } = filledArc.getProps([
        "startAngle",
        "endAngle",
      ]);
      const midAngle = (startAngle + endAngle) / 2;
      const arcMidRadius = (innerRadius + outerRadius) / 2;
      const labelX = centerX + Math.cos(midAngle) * arcMidRadius;
      const labelY = centerY + Math.sin(midAngle) * arcMidRadius;

      ctx.save();
      ctx.font = `700 14px ${FONT_FAMILY}`;
      ctx.fillStyle = color;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(value + "%", labelX, labelY);
      ctx.restore();
    }
  },
};

/* ============================================================== *
 * Entry point — dispatcher
 * ============================================================== */
export default function CustomDonutChart(props) {
  const { data, loading, error, title } = props;

  // Loading state — show a simple spinner-style placeholder
  if (loading) {
    return (
      <div className={`${styles.cardRate} ${styles.cardBreakdown}`}>
        <div className={styles.header}>
          <div className={styles.titleWrap}>
            <svg
              className={styles.titleIcon}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M3 12h3l2-5 4 10 2-5h7" />
            </svg>
            <h2 className={styles.title}>{title}</h2>
          </div>
        </div>
        <div
          className={styles.body}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 240,
            color: "var(--text-muted, #6c757d)",
            fontSize: 14,
          }}
        >
          Loading…
        </div>
      </div>
    );
  }

  // Error state — surface the API message (or fallback) where the donut would be
  if (error) {
    return (
      <div className={`${styles.cardRate} ${styles.cardBreakdown}`}>
        <div className={styles.header}>
          <div className={styles.titleWrap}>
             <svg
            className={styles.titleIcon}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M3 12h3l2-5 4 10 2-5h7" />
          </svg>
            <h2 className={styles.title}>{title}</h2>
          </div>
        </div>
        <div
          className={styles.body}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 240,
            color: "var(--text-muted, #6c757d)",
            fontSize: 14,
            textAlign: "center",
            padding: "0 24px",
          }}
        >
          {error}
        </div>
      </div>
    );
  }

  const isSegments = Array.isArray(data?.segments);
  const isBreakdown = data?.breakdown && typeof data.breakdown === "object";

  if (isSegments) return <SegmentsDonut {...props} />;
  if (isBreakdown) return <BreakdownDonut {...props} />;
  return <SingleArcDonut {...props} />;
}

/* ============================================================== *
 * MODE 1 — Single-arc donut (Attendance Rate fraction)
 * ============================================================== */
function SingleArcDonut({
  data,
  filterOptions = ["All"],
  onFilterChange = () => { },
  title = "Attendance Rate",
}) {
  const { presentDays = 0, workingDays = 0, filter = "All" } = data || {};

  const overall = useMemo(() => {
    if (typeof data?.overall === "number" && !Number.isNaN(data.overall)) {
      return data.overall;
    }
    if (workingDays > 0) return Math.round((presentDays / workingDays) * 100);
    return 0;
  }, [data, presentDays, workingDays]);

  const [theme, setTheme] = useState({
    arcDark: "#10B981",
    arcLight: "#6EE7B7",
    arcTrack: "#f1f1f1",
    text: "#142454",
    muted: "#999999",
  });

  useEffect(() => {
    const { dark, light } = resolveGradient(1);
    setTheme({
      arcDark: dark,
      arcLight: light,
      arcTrack: readToken("--color-neutral-400", "#f1f1f1"),
      text: readToken("--panel-dark-bg", "#142454"),
      muted: readToken("--text-muted-light", "#999999"),
    });
  }, []);

  const chartData = useMemo(
    () => ({
      labels: ["Present", "Remaining"],
      datasets: [
        {
          data: [overall, Math.max(0, 100 - overall)],
          backgroundColor: (ctx) => {
            const { chart, dataIndex } = ctx;
            const { ctx: canvasCtx, chartArea } = chart;
            if (dataIndex !== 0) return "#ffffff";
            return (
              makeArcGradient(
                canvasCtx,
                chartArea,
                theme.arcDark,
                theme.arcLight,
              ) || theme.arcDark
            );
          },
          borderColor: ["transparent", theme.arcTrack],
          borderWidth: [0, 1],
          cutout: "62%",
        },
      ],
    }),
    [overall, theme.arcDark, theme.arcLight, theme.arcTrack],
  );

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: true,
      animation: { animateRotate: true, duration: 800, easing: "easeOutCubic" },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx) =>
              ctx.dataIndex === 0
                ? `Present: ${overall}%`
                : `Remaining: ${100 - overall}%`,
          },
        },
        donutCenter: {
          fraction: {
            numerator: presentDays,
            denominator: workingDays,
            numeratorColor: theme.text,
            denominatorColor: theme.muted,
          },
          arcLabel: { value: overall, color: theme.text },
        },
      },
    }),
    [overall, presentDays, workingDays, theme.text, theme.muted],
  );

  const dropdownOptions = useMemo(
    () => filterOptions.map((opt) => ({ label: opt, value: opt })),
    [filterOptions],
  );

  const selectedOption = useMemo(
    () => dropdownOptions.find((o) => o.value === filter) || null,
    [dropdownOptions, filter],
  );

  const handleDropdownChange = (option) => {
    onFilterChange(option ? option.value : null);
  };

  return (
    <div className={styles.cardRate}>
      <div className={styles.header}>
        <div className={styles.titleWrap}>
          <svg
            className={styles.titleIcon}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M3 12h3l2-5 4 10 2-5h7" />
          </svg>
          <h2 className={styles.title}>{title}</h2>
        </div>

        <div className={styles.filterWrap}>
          <CustomDropdown
            name="donut-filter"
            value={selectedOption}
            onChange={handleDropdownChange}
            options={dropdownOptions}
            placeholder="All"
            isSearchable={false}
            isClearable={false}
            size="sm"
          />
        </div>
      </div>

      <div className={styles.body}>
        <div className={styles.chartWrap}>
          <Doughnut
            data={chartData}
            options={chartOptions}
            plugins={[donutCenterPlugin]}
          />
        </div>
      </div>
    </div>
  );
}

/* ============================================================== *
 * MODE 2 — Multi-segment donut with legend (Academic Health Score)
 * ============================================================== */
function BreakdownDonut({
  data,
  title = "Academic Health Score",
  segmentConfig = DEFAULT_SEGMENT_CONFIG,
  status,
}) {
  const { compositeScore = 0, breakdown = {}, aiSummary = "" } = data || {};
  const effectiveStatus = status || data?.status || "";

  const [gradients, setGradients] = useState({});
  const [theme, setTheme] = useState({
    text: "#142454",
    scoreColor: "#10B981",
  });

  useEffect(() => {
    const next = {};
    Object.entries(segmentConfig).forEach(([key, cfg]) => {
      next[key] = resolveGradient(cfg.paletteIndex ?? 0);
    });
    setGradients(next);

    const statusPaletteIdx = STATUS_PALETTE_INDEX[effectiveStatus] ?? 1;
    setTheme({
      text: readToken("--panel-dark-bg", "#142454"),
      scoreColor: resolveGradient(statusPaletteIdx).dark,
    });
  }, [segmentConfig, effectiveStatus]);

  const segmentKeys = useMemo(() => Object.keys(breakdown), [breakdown]);

  const chartData = useMemo(
    () => ({
      labels: segmentKeys.map((k) => segmentConfig[k]?.label || k),
      datasets: [
        {
          data: segmentKeys.map((k) => breakdown[k]?.weight || 0),
          backgroundColor: (ctx) => {
            const { chart, dataIndex } = ctx;
            const { ctx: canvasCtx, chartArea } = chart;
            const key = segmentKeys[dataIndex];
            const g = gradients[key];
            if (!g) return "#cccccc";
            return (
              makeArcGradient(canvasCtx, chartArea, g.dark, g.light) || g.dark
            );
          },
          borderColor: "#ffffff",
          borderWidth: 2,
          hoverOffset: 6,
          cutout: "55%",
        },
      ],
    }),
    [segmentKeys, breakdown, gradients, segmentConfig],
  );

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: true,
      animation: { animateRotate: true, duration: 800, easing: "easeOutCubic" },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx) => {
              const key = segmentKeys[ctx.dataIndex];
              const item = breakdown[key];
              if (!item) return ctx.label;
              const contribution = Math.round((item.score * item.weight) / 100);
              return [
                `Score: ${item.score}/100`,
                `Weightage: ${item.weight}%`,
                `Contribution: ${contribution} pts`,
              ];
            },
          },
        },
        segmentLabels: { color: theme.text, font: `700 16px ${FONT_FAMILY}` },
        donutCenter: {
          score: { value: compositeScore, color: theme.scoreColor },
        },
      },
    }),
    [segmentKeys, breakdown, compositeScore, theme.text, theme.scoreColor],
  );

  return (
    <div className={`${styles.cardRate} ${styles.cardBreakdown}`}>
      <div className={styles.header}>
        <div className={styles.titleWrap}>
          <svg
            className={styles.titleIcon}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M7 12h2l1.5-3 3 6 1.5-3H17" />
          </svg>
          <h2 className="chartTitle">{title}</h2>
        </div>
      </div>

      <div className={styles.body}>
        <div className={styles.chartWrap}>
          <Doughnut
            data={chartData}
            options={chartOptions}
            plugins={[segmentLabelsPlugin, donutCenterPlugin]}
          />
        </div>

        <div className={styles.legend} role="list">
          {segmentKeys.map((key) => {
            const cfg = segmentConfig[key];
            if (!cfg) return null;
            const g = gradients[key];
            const swatchBg = g?.cssString || "#cccccc";
            return (
              <span key={key} className={styles.legendItem} role="listitem">
                <span
                  className={styles.legendSwatch}
                  style={{ background: swatchBg }}
                  aria-hidden="true"
                />
                {cfg.label}
              </span>
            );
          })}
        </div>
      </div>

      {aiSummary && (
        <div className={styles.aiSummary}>
          <span className={styles.aiSummaryLabel}>AI Insight:</span>
          {aiSummary}
        </div>
      )}
    </div>
  );
}

/* ============================================================== *
 * MODE 3 — Segments donut with counts + legend (Daily Attendance Rate)
 * --------------------------------------------------------------
 * Triggered when `data.segments` is an array.
 *
 * Data shape:
 *   {
 *     total: number,                  // drawn plain in center (no %)
 *     segments: [
 *       { key, label, value, paletteIndex }
 *     ],
 *     centerColor?: string            // optional center number color override
 *   }
 * ============================================================== */
function SegmentsDonut({ data, title = "Attendance Rate" }) {
  const { segments = [], total = 0, centerColor } = data || {};

  const [resolved, setResolved] = useState({
    gradients: [],
    textColor: "#142454",
  });

  useEffect(() => {
    const grads = segments.map((s) => resolveGradient(s.paletteIndex ?? 0));
    setResolved({
      gradients: grads,
      textColor: readToken("--panel-dark-bg", "#142454"),
    });
  }, [segments]);

  const chartData = useMemo(
    () => ({
      labels: segments.map((s) => s.label),
      datasets: [
        {
          data: segments.map((s) => Number(s.value) || 0),
          backgroundColor: (ctx) => {
            const { chart, dataIndex } = ctx;
            const { ctx: canvasCtx, chartArea } = chart;
            const g = resolved.gradients[dataIndex];
            if (!g) return "#cccccc";
            return (
              makeArcGradient(canvasCtx, chartArea, g.dark, g.light) || g.dark
            );
          },
          borderColor: "#ffffff",
          borderWidth: 2,
          hoverOffset: 6,
          cutout: "58%",
        },
      ],
    }),
    [segments, resolved.gradients],
  );

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: true,
      animation: { animateRotate: true, duration: 800, easing: "easeOutCubic" },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx) => `${ctx.label}: ${ctx.parsed}`,
          },
        },
        segmentCount: { color: "#ffffff", font: `700 14px ${FONT_FAMILY}` },
        donutCenter: {
          plainValue: {
            value: total,
            color: centerColor || resolved.textColor,
          },
        },
      },
    }),
    [total, centerColor, resolved.textColor],
  );

  return (
    <div className={`${styles.cardRate} ${styles.cardBreakdown}`}>
      <div className={styles.header}>
        <div className={styles.titleWrap}>
          <svg
            className={styles.titleIcon}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M3 12h3l2-5 4 10 2-5h7" />
          </svg>
          <h2 className={styles.title}>{title}</h2>
        </div>
      </div>

      <div className={styles.body}>
        <div className={styles.chartWrap}>
          <Doughnut
            data={chartData}
            options={chartOptions}
            plugins={[segmentCountPlugin, donutCenterPlugin]}
          />
        </div>

        <div className={styles.legend} role="list">
          {segments.map((seg, idx) => {
            const swatchBg = resolved.gradients[idx]?.cssString || "#cccccc";
            return (
              <span
                key={seg.key || idx}
                className={styles.legendItem}
                role="listitem"
              >
                <span
                  className={styles.legendSwatch}
                  style={{ background: swatchBg }}
                  aria-hidden="true"
                />
                {seg.label}
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
}
